import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Switch,
  Alert,
  Avatar,
  Checkbox,
  DialogActions,
  ListItem,
  ListItemText,
  FormControlLabel,
  FormHelperText,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
  ASSETICONBASEURL,
  showToast,
  tabsStyle,
  WEBSERVICE,
} from "../../../CoreDataService";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CustomCircleCheck from "../../../Common/CircleCheck";
import DataTable from "../../../Common/DataTable";
import { HeaderText, SubTitleText } from "../../../Common/AllText";
import { ContainedButton, OutlinedButton } from "../../../Common/Buttons";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1"];

// ── Design-system style objects ──────────────────────────────────────────────
const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = (btncolor) => ({
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
  background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
});

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 440 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.15rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
};

const modernFieldStyle = {
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiSelect-icon": { color: "var(--text-color)" },
};

// multiline-friendly variant (no fixed height)
const modernFieldStyleMultiline = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.7 },
};

// plan card used in Multi-asset Products tab
const planCard = {
  p: 2.5,
  borderRadius: "14px",
  backgroundColor: "var(--body-color-less)",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  transition: "box-shadow 0.2s ease",
  "&:hover": { boxShadow: "0 4px 18px rgba(0,0,0,0.12)" },
};
// ─────────────────────────────────────────────────────────────────────────────

const PortfolioInvestPage = () => {
  const theme = useTheme();
  const themeColor = localStorage.getItem("theme");
  const { btncolor } = useSelector((state) => state);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { brokerBasicInfo } = useSelector((state) => state);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedTimeFrames, setSelectedTimeFrames] = useState({});
  const [open, setOpen] = useState(false);
  const [allocations, setAllocations] = useState([{ asset: "", percent: "" }]);
  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedRecurring, setSelectedRecurring] = useState("");
  const [error, setError] = useState("");
  const [portfolioInvestmentRois, setPortfolioInvestmentRois] = useState([]);
  const [selectedRoiIteration, setSelectedRoiIteration] = useState({});
  const [portfolioInvestmentPlans, setPortfolioInvestmentPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const accessToken = localStorage.getItem("access_token");
  const uuid = localStorage.getItem("uuid");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [portfolioPlanDetails, setPortfolioPlanDetails] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [portFolioCreateModal, setPortFolioCreateModal] = useState(false);

  const [planName, setPlanName] = useState("");
  const [assets, setAssets] = useState([{ index: 0 }]);
  const [recurringAmount, setRecurringAmount] = useState("");
  const [recurringAssetId, setRecurringAssetId] = useState("");
  const [selectedWallet, setSelectedWallet] = useState("spot");
  const [isOneTime, setIsOneTime] = useState(false);
  const [selectedRecurringPeriodId, setSelectedRecurringPeriodId] =
    useState("");
  const [daysSelected, setDaysSelected] = useState([]);
  const [dateOfMonth, setDateOfMonth] = useState("");
  const [localTimeHour, setLocalTimeHour] = useState("");
  const [localTimeMin, setLocalTimeMin] = useState("");

  const [recurringPeriodsOther, setRecurringPeriodsOther] = useState([]);
  const [recurringPeriodsHours, setRecurringPeriodsHours] = useState([]);
  const daysOfWeeks = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const [portfolioAssets, setPortfolioAssets] = useState([]);
  const [portfolioAssetsForBase, setPortfolioAssetsForBase] = useState([]);
  const [portfolioAssetsForCounter, setPortfolioAssetsForCounter] = useState(
    []
  );

  const [earnWalletBalance, setEarnWalletBalance] = useState(0);
  const [spotWalletBalance, setSpotWalletBalance] = useState(0);
  const [masterInvestmentId, setMasterInvestmentId] = useState(0);
  const [errors, setErrors] = useState({});
  const [successModal, setSuccessModal] = useState(false);
  const [createPortfolioPayload, setCreatePortfolioPayload] = useState({});

  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [termsLink, setTermsLink] = useState(
    window.location.origin + "/legal/portfolio-invest.html"
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: `BEARER ${accessToken}`,
  };

  useEffect(() => {
    getAllInvestmentRoi();
    getAllMultiAssetProduct();
    getAllRecurringPeriod();
    getAllAvailablePortfolioAssets();
  }, []);

  const getAllInvestmentRoi = () => {
    const payload = {
      userUuid: uuid,
      invDuration: "3M",
    };

    axios
      .post(
        WEBSERVICE + "/crypto/banking/portfolio/getPortfolioInvestmentsRoi",
        payload,
        { headers }
      )
      .then((res) => {
        if (res.data.error.error_data !== 0) {
          console.error(res.data.error.error_msg);
        } else {
          setPortfolioInvestmentRois(res.data.portfolioInvestmentRois);
          const allRoiIteration = res.data.portfolioInvestmentRois?.map(
            (a) => ({ ...a, duration: "3M" })
          );
          setSelectedRoiIteration(allRoiIteration);
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          console.error("Could not connect to server");
        }
      });
  };

  const getRoiByCurrency = (item, duration, i) => {
    setSelectedRoiIteration((prevData) => {
      const updated = [...prevData];
      updated[i] = {
        ...updated[i],
        duration: duration,
      };
      return updated;
    });

    const payload = {
      userUuid: uuid,
      invDuration: duration,
      invCurrencyId: item.currencyId,
    };

    axios
      .post(
        WEBSERVICE + "/crypto/banking/portfolio/getPortfolioInvestmentRoi",
        payload,
        { headers }
      )
      .then((res) => {
        if (res.data.error.error_data !== 0) {
          console.error(res.data.error.error_msg);
        } else {
          const updatedRois = portfolioInvestmentRois.map((roi) =>
            roi.currencyCode === item.currencyCode
              ? {
                  ...roi,
                  returnOnInvestment:
                    res.data.portfolioInvestmentRoi.returnOnInvestment,
                }
              : roi
          );
          setPortfolioInvestmentRois(updatedRois);
        }
      })
      .catch((err) => {
        if ([401, 400, 403].includes(err.response?.status)) {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", "Could Not Connect To Server");
        }
      });
  };

  const getAllRecurringPeriod = async () => {
    setLoading(true);
    try {
      const uuid = localStorage.getItem("uuid");
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${WEBSERVICE}crypto/banking/portfolio/getPortfolioRecurringPeriods`,
        {
          params: { uuid },
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      const result = response.data;

      if (result.error.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        const other = [];
        const hours = [];

        result.portfolioRecurringPeriods.forEach((period) => {
          if (period.name.includes("Hour")) {
            other.push(period);
          } else {
            hours.push(period);
          }
        });

        setRecurringPeriodsOther(other);
        setRecurringPeriodsHours(hours);
      }
    } catch (error) {
      const status = error.response?.status;
      if ([401, 400, 403].includes(status)) {
        localStorage.clear();
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Could Not Connect To Server");
      }
    } finally {
      setLoading(false);
    }
  };

  const getAllAvailablePortfolioAssets = async () => {
    setLoading(true);
    try {
      const payload = {
        userUuid: localStorage.getItem("uuid"),
      };

      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/portfolio/getPortfolioInvestmentAssets`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const result = response.data;

      if (result.error?.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        const allAssets = result.portfolioAssets || [];
        const base = allAssets.filter((asset) => asset.isBaseCurrency === 1);
        const counter = allAssets.filter((asset) => asset.isBaseCurrency === 0);

        setPortfolioAssets(allAssets);
        setPortfolioAssetsForBase(base);
        setPortfolioAssetsForCounter(counter);
      }
    } catch (error) {
      if ([400, 401, 403].includes(error.response?.status)) {
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Could Not Connect To Server");
      }
    } finally {
      setLoading(false);
    }
  };

  const getWalletBalance = async (val) => {
    setRecurringAssetId(val);

    setLoading(true);
    const payload = {
      userUuid: localStorage.getItem("uuid"),
      invCurrencyId: val,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/portfolio/getWalletDetailsByUser`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const result = response.data;

      if (result.error?.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        setEarnWalletBalance(result.earnWalletBalance);
        setSpotWalletBalance(result.spotWalletBalance);
      }
    } catch (error) {
      if ([400, 401, 403].includes(error.response?.status)) {
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Could Not Connect To Server");
      }
    } finally {
      setLoading(false);
    }
  };

  const getAllMultiAssetProduct = () => {
    setLoading(true);
    const payload = {
      userUuid: localStorage.getItem("uuid"),
    };

    axios
      .post(
        WEBSERVICE + "/crypto/banking/portfolio/getPortfolioInvestmentPlans",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then((response) => {
        const result = response.data;
        setLoading(false);

        if (result.error.error_data !== 0) {
          setErrorMsg(result.error.error_msg);
        } else {
          const plans = result.portfolioInvestmentPlans.map((plan) => ({
            ...plan,
            assets: plan.planAssetCodes.split(","),
          }));

          plans.sort((a, b) => a.planOrder - b.planOrder);

          setPortfolioInvestmentPlans(plans);
          console.log("portfolioInvestmentPlans", plans);
        }
      })
      .catch((error) => {
        setLoading(false);

        if ([401, 400, 403].includes(error.response?.status)) {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", "Could Not Connect To Server");
        }
      });
  };

  const handleInvest = () => {
    if (!isValidAllocation) {
      setError("Total allocation must equal 100%");
      return;
    }
    setError("");
    setOpen(false);
  };

  const renderTimeframeButton = (code, duration, item, i) => {
    const isSelected =
      selectedRoiIteration[i].currencyCode === code &&
      selectedRoiIteration[i].duration === duration;
    return (
      <Button
        key={duration}
        variant={isSelected ? "contained" : "outlined"}
        sx={{
          bgcolor: isSelected ? btncolor : "transparent",
          borderColor: !isSelected ? btncolor : btncolor,
          color: isSelected ? "#fff" : "var(--text-color)",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "0.75rem",
          minWidth: 48,
          mr: 0.75,
          "&:hover": {
            bgcolor: isSelected ? btncolor : alpha(btncolor || "#1976d2", 0.1),
          },
        }}
        size="small"
        onClick={() => getRoiByCurrency(item, duration, i)}
      >
        {duration}
      </Button>
    );
  };

  const openViewAllocationModal = async (plan) => {
    try {
      setSelectedPlan(plan);
      const payload = {
        userUuid: localStorage.getItem("uuid"),
        portfolioInvestmentPlan: { id: plan.id },
      };

      const response = await axios.post(
        WEBSERVICE + "/crypto/banking/portfolio/getPortfolioInvestmentPlan",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const result = response.data;
      if (result.error?.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        const assets = result.portfolioInvestmentAssets.map((item) => ({
          name: item.currencyCode,
          value: item.assetAllocation,
        }));
        setPortfolioPlanDetails(assets);
        setOpen(true);
      }
    } catch (err) {
      if ([400, 401, 403].includes(err.response?.status)) {
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Could Not Connect To Server");
      }
    } finally {
      document.body.classList.remove("overlay");
    }
  };

  const getCurrencyNameById = (id) => {
    console.log(id, portfolioAssets);
    let obj = {};
    for (let i = 0; i < portfolioAssets.length; i++) {
      console.log(id, portfolioAssets[i].currencyId);
      if (id === portfolioAssets[i].currencyId) {
        obj = portfolioAssets[i];
        break;
      }
    }
    return obj;
  };

  const handleClose = () => setOpen(false);

  const clickToCopy = (text) => {
    setMasterInvestmentId(text);
    navigator.clipboard
      .writeText(text)
      .then(() => showToast("success", "Copied!"));
  };

  const addAsset = () => {
    setAssets((prev) => [
      ...prev,
      {
        index: prev.length,
        currencyId: "",
        assetAllocation: "",
      },
    ]);
  };

  const removeAsset = (indexToRemove) => {
    setAssets(assets.filter((a) => a.index !== indexToRemove));
  };

  const toggleDay = (day) => {
    setDaysSelected((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  {
    /* Get selected period name */
  }
  const getSelectedPeriodName = () => {
    const allPeriods = [...recurringPeriodsOther, ...recurringPeriodsHours];
    const selected = allPeriods.find((p) => p.id === selectedRecurringPeriodId);
    console.log("tttstttss", selected);

    return selected?.name?.toLowerCase() || "";
  };

  const selectedPeriodName = getSelectedPeriodName();
  const isWeekly =
    selectedPeriodName.includes("weekly") || selectedPeriodName.includes("bi");
  const isMonthly = selectedPeriodName.includes("month");
  const needsTime =
    selectedPeriodName.includes("hour") ||
    selectedPeriodName.includes("time") ||
    isMonthly;

  const totalAllocation = assets.reduce(
    (sum, asset) => sum + Number(asset.assetAllocation || 0),
    0
  );
  const isValidAllocation = totalAllocation === 100;

  const formatTime = `${String(localTimeHour).padStart(2, "0")}:${String(
    localTimeMin
  ).padStart(2, "0")}`;
  const today = dayjs();
  let firstCycleMessage = "";

  if (["4 hours", "8 hours", "12 hours"].includes(selectedPeriodName)) {
    firstCycleMessage = `Your first auto-invest cycle will begin on at ${formatTime}`;
  } else if (isMonthly) {
    const dateString = today.date(Number(dateOfMonth)).format("YYYY-MM-DD");
    firstCycleMessage = `Your first auto-invest cycle will begin on ${dateString} at ${formatTime}`;
  } else {
    const dateString = today.format("YYYY-MM-DD");
    firstCycleMessage = `Your first auto-invest cycle will begin on ${dateString} at ${formatTime}`;
  }

  const openPortfolioEditModal = async (id) => {
    setModalMode("edit");
    getSpecificPortfolioDetails(id);
  };

  const openPortFolioCreateModal = async () => {
    setModalMode("create");
    setAssets([{ index: 0 }]);
    setPortFolioCreateModal(true);
  };

  const getSpecificPortfolioDetails = async (id) => {
    try {
      const payload = {
        userUuid: localStorage.getItem("uuid"),
        portfolioInvestmentPlan: { id },
      };

      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/portfolio/getPortfolioInvestmentPlan`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const result = response.data;

      if (result.error?.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        const data = result;
        const assetsData = data.portfolioInvestmentAssets || [];

        const formattedAssets = assetsData.map((asset, index) => ({
          index,
          currencyId: asset.currencyId,
          assetAllocation: asset.assetAllocation,
        }));
        setAssets(formattedAssets);

        setPlanName("");
        setRecurringAmount("");
        setRecurringAssetId("");
        setSelectedWallet("spot");
        setIsOneTime(false);
        setSelectedRecurringPeriodId("");
        setDaysSelected([]);
        setDateOfMonth("");
        setLocalTimeHour("");
        setLocalTimeMin("");

        setPortFolioCreateModal(true);
      }
    } catch (error) {
      console.error(error);
      if ([401, 400, 403].includes(error.response?.status)) {
        showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
      } else {
        showToast("error", "Could Not Connect To Server");
      }
    }
  };

  console.log("assets--->", assets, portfolioAssetsForCounter);

  const handleCreatePortfolio = async () => {
    const newErrors = {};

    if (!planName || planName.trim().length < 3) {
      newErrors.planName = "Plan name must be at least 3 characters.";
    }

    if (
      !recurringAmount ||
      isNaN(recurringAmount) ||
      Number(recurringAmount) <= 0
    ) {
      newErrors.recurringAmount = "Enter a valid recurring amount.";
    }

    if (!recurringAssetId) {
      newErrors.recurringAssetId = "Select a recurring asset.";
    }

    assets.forEach((asset, i) => {
      if (!asset.currencyId) {
        newErrors[`asset_${i}_currencyId`] = "Asset required.";
      }
      if (
        asset.assetAllocation === "" ||
        isNaN(asset.assetAllocation) ||
        Number(asset.assetAllocation) < 1 ||
        Number(asset.assetAllocation) > 100
      ) {
        newErrors[`asset_${i}_allocation`] = "Enter a valid % (1-100).";
      }
    });

    if (totalAllocation !== 100) {
      newErrors.totalAllocation = "Total allocation must equal 100%.";
    }

    if (
      isMonthly &&
      (isNaN(dateOfMonth) || dateOfMonth < 1 || dateOfMonth > 31)
    ) {
      newErrors.dateOfMonth = "Date must be between 1 and 31.";
    }

    if (localTimeHour < 0 || localTimeHour > 23 || isNaN(localTimeHour)) {
      newErrors.localTimeHour = "Hour must be between 0–23.";
    }

    if (localTimeMin < 0 || localTimeMin > 59 || isNaN(localTimeMin)) {
      newErrors.localTimeMin = "Minute must be between 0–59.";
    }
    if (!agreeTerms) {
      newErrors.agreeTerms = "Please check invest agreement";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const userUuid = localStorage.getItem("uuid");
    const accessToken = localStorage.getItem("access_token");

    let investment = {
      userUuid: userUuid,
      planName: planName,
      invCurrencyId: recurringAssetId,
      investmentAmount: recurringAmount,
      isEarnWallet: selectedWallet === "earn" ? 1 : 0,
      isSubscription: isOneTime ? 1 : 0,
      recurringPeriodId: selectedRecurringPeriodId,
      repeatsOn: null,
      masterInvestmentId: masterInvestmentId,
      localTime: `${localTimeHour}:${localTimeMin}`,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (isWeekly) {
      investment.repeatsOn = daysSelected.map((d) => d.toUpperCase()).join(",");
    } else if (isMonthly) {
      investment.repeatsOn = dateOfMonth;
    }

    let investmentAssets = [];
    let totalPercentage = 0;

    for (const asset of assets) {
      const currencyId = asset.currencyId;
      const allocation = parseFloat(asset.assetAllocation || 0);

      if (!currencyId || isNaN(allocation)) continue;

      investmentAssets.push({ currencyId, assetAllocation: allocation });
      totalPercentage += allocation;
    }

    investment.investmentAssets = investmentAssets;

    if (totalPercentage !== 100) {
      showToast("error", "Sum of asset allocation percentages must be 100%");
      return;
    }

    console.log("payload", investment);

    setCreatePortfolioPayload(investment);

    try {
      const response = await fetch(
        WEBSERVICE + "/crypto/banking/portfolio/createPortfolioInvestment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${accessToken}`,
          },
          body: JSON.stringify({ investment }),
        }
      );

      const result = await response.json();

      if (result.error?.error_data !== 0) {
        showToast(
          "error",
          result.error.error_msg || "Failed to create portfolio."
        );
      } else {
        showToast("success", "Portfolio created successfully");
        handleClosePortFolioCreateModal();
        setSuccessModal(true);
      }
    } catch (err) {
      showToast("error", "Could not connect to server");
    }
  };

  const handleClosePortFolioCreateModal = () => {
    setPortFolioCreateModal(false);
    resetFormData();
  };

  const isFormValid = () => {
    if (!planName) return false;
    if (assets.length === 0) return false;
    if (totalAllocation !== 100) return false;
    if (
      assets.some(
        (a) => !a.currencyId || !a.assetAllocation || a.assetAllocation < 10
      )
    )
      return false;
    if (
      !recurringAmount ||
      isNaN(recurringAmount) ||
      Number(recurringAmount) <= 0
    )
      return false;
    if (!recurringAssetId) return false;
    if (!localTimeHour && localTimeHour !== 0) return false;
    if (!localTimeMin && localTimeMin !== 0) return false;
    if (!agreeTerms) return false;
    if (!selectedRecurringPeriodId) return false;
    if (
      !(
        Number(recurringAmount) < Number(spotWalletBalance) ||
        Number(recurringAmount) < Number(earnWalletBalance)
      )
    )
      return false;

    return true;
  };

  const resetFormData = () => {
    setPlanName("");
    setRecurringAmount("");
    setRecurringAssetId("");
    setSelectedWallet("spot");
    setIsOneTime(false);
    setSelectedRecurringPeriodId("");
    setLocalTimeHour("");
    setLocalTimeMin("");
    setSpotWalletBalance(0);
  };

  const handleHourChange = (e) => {
    const value = e.target.value;
    setLocalTimeHour(value);
    if (value === "" || isNaN(value) || value < 0 || value > 23) {
      setErrors((prev) => ({
        ...prev,
        localTimeHour: "Hour must be between 0 and 23",
      }));
    } else {
      setErrors((prev) => ({ ...prev, localTimeHour: "" }));
    }
  };

  const handleMinChange = (e) => {
    const value = e.target.value;
    setLocalTimeMin(value);
    if (value === "" || isNaN(value) || value < 0 || value > 59) {
      setErrors((prev) => ({
        ...prev,
        localTimeMin: "Minute must be between 0 and 59",
      }));
    } else {
      setErrors((prev) => ({ ...prev, localTimeMin: "" }));
    }
  };

  const allowNumber = (e) => {
    const key = e.key;
    const value = e.target.value;

    if (
      key === "Backspace" ||
      key === "Tab" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Delete"
    ) {
      return;
    }

    const regex = /^[0-9]$/;

    if (key === ".") {
      if (value.includes(".")) {
        e.preventDefault();
      }
      return;
    }

    if (!regex.test(key)) {
      e.preventDefault();
    }
  };

  const allowOnlyAlphabets = (e) => {
    const key = e.key;
    if (
      key === "Backspace" ||
      key === "Tab" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Delete"
    ) {
      return;
    }
    const regex = /^[a-zA-Z\s]$/;
    if (!regex.test(key)) {
      e.preventDefault();
    }
  };

  const handlePasteOnlyAlpha = (e) => {
    const pasteData = e.clipboardData.getData("text");
    const regex = /^[a-zA-Z\s]$/;

    if (!regex.test(pasteData)) {
      e.preventDefault();
    }
  };

  const allowOnlyAlphaNum = (e) => {
    const key = e.key;
    if (
      key === "Backspace" ||
      key === "Tab" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Delete"
    ) {
      return;
    }
    const regex = /^[a-zA-Z0-9 ]*$/;
    if (!regex.test(key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text");
    const regex = /^[a-zA-Z0-9 ]*$/;

    if (!regex.test(pasteData)) {
      e.preventDefault();
    }
  };

  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box sx={heroBanner(btncolor)}>
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AccountBalanceWalletOutlinedIcon
              sx={{ color: "#fff", fontSize: 28 }}
            />
          </Box>
          <Box>
            <Typography sx={heroTitle}>Portfolio Investment</Typography>
            <Typography sx={heroSubtitle}>
              Build and manage a diversified crypto portfolio — set recurring
              investment plans, track allocations, and grow your holdings
              automatically.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Feature highlights + CTA ── */}
      <Box sx={{ ...mainCard, mb: 3, p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={1.25} mb={2.5}>
          {[
            "Regularly invest in cryptocurrency with a pre-determined amount and accumulate your crypto holdings.",
            "Pick a coin you want to invest in, decide your investment amount and schedule a recurring plan.",
            "Once invested, you can receive daily earnings from interest earn and redeem your funds any time you wish.",
          ].map((text, i) => (
            <Box display="flex" alignItems="flex-start" gap={1.25} key={i}>
              <CustomCircleCheck bgColor={btncolor} />
              <Typography
                variant="body2"
                sx={{ color: "var(--text-color)", lineHeight: 1.6 }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Stack>

        <ContainedButton
          variant="contained"
          onClick={() => openPortFolioCreateModal()}
          sx={{ borderRadius: "10px", fontWeight: 600, px: 3 }}
        >
          Create your portfolio
        </ContainedButton>
      </Box>

      {/* ── Main content card ── */}
      <Box sx={mainCard}>
        {/* Tabs */}
        <Box sx={tabsWrap}>
          <Tabs
            value={tabIndex}
            onChange={(e, val) => setTabIndex(val)}
            textColor="inherit"
            sx={{
              ...tabsStyle(btncolor),
              "& .MuiTab-root": tabItem,
            }}
          >
            <Tab label="All Assets" />
            <Tab label="Multi-asset Products" />
          </Tabs>
        </Box>

        {/* Tab 0 — All Assets */}
        {tabIndex === 0 && (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <TableContainer sx={modernTableContainer} component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={modernTableHeadRow}>
                    {["Product", "Choose Time Frame", "Historical ROI (%)"].map(
                      (col) => (
                        <TableCell key={col} sx={modernTableHeadCell}>
                          {col}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolioInvestmentRois.map((item, i) => (
                    <TableRow key={item.currencyCode} sx={modernTableBodyRow}>
                      <TableCell
                        sx={{
                          ...modernTableBodyCell,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <img
                          src={`${ASSETICONBASEURL}${item.currencyCode}.png`}
                          alt={item.currencyCode}
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            color: "var(--text-color)",
                          }}
                        >
                          {item.currencyCode}
                        </Typography>
                      </TableCell>
                      <TableCell sx={modernTableBodyCell}>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {["3M", "6M", "1YR"].map((duration) =>
                            renderTimeframeButton(
                              item.currencyCode,
                              duration,
                              item,
                              i
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        id={`roiFor${item.currencyCode}`}
                        sx={{
                          ...modernTableBodyCell,
                          fontWeight: 600,
                          color: btncolor || "var(--text-color)",
                        }}
                      >
                        {item.returnOnInvestment}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 1 — Multi-asset Products */}
        {tabIndex === 1 && (
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Grid container spacing={2}>
              {portfolioInvestmentPlans.map((item) => (
                <Grid
                  size={isMobile ? 12 : 4}
                  xs={12}
                  key={item.planName}
                >
                  <Box sx={planCard}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: "var(--text-color)", mb: 1.25 }}
                    >
                      {item.planName}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--text-color)",
                        opacity: 0.6,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 600,
                      }}
                    >
                      Assets
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ my: 1.25 }}
                      flexWrap="wrap"
                    >
                      {item.assets.map((symbol) => (
                        <Avatar
                          key={symbol}
                          src={`${ASSETICONBASEURL}${symbol}.png`}
                          alt={symbol}
                          sx={{ width: 28, height: 28 }}
                        />
                      ))}
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ color: "var(--text-color)", opacity: 0.8, mb: 2 }}
                    >
                      No. of Transactions:{" "}
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "var(--text-color)" }}
                      >
                        {item.transactionCount}
                      </Box>
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <OutlinedButton
                        size="small"
                        variant="outlined"
                        onClick={() => openViewAllocationModal(item)}
                        sx={{ borderRadius: "8px", fontWeight: 600 }}
                      >
                        View Allocation
                      </OutlinedButton>
                      <ContainedButton
                        size="small"
                        variant="contained"
                        onClick={() => openPortfolioEditModal(item.id)}
                        sx={{ borderRadius: "8px", fontWeight: 600 }}
                      >
                        Copy
                      </ContainedButton>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      {/* ── Create / Edit Portfolio Dialog ── */}
      <Dialog
        open={portFolioCreateModal}
        onClose={handleClosePortFolioCreateModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { ...dialogPaper, minWidth: { xs: "90%", sm: 600 } } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...dialogTitleStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
            }}
          >
            <Box sx={{ width: 32 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "var(--text-color)" }}
            >
              Create Your Investment Portfolio
            </Typography>
            <IconButton
              onClick={handleClosePortFolioCreateModal}
              size="small"
              sx={{ color: "var(--text-color)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            pt: 3,
            backgroundColor: "var(--popupbody-color)",
            borderColor: "rgba(125,125,125,0.15)",
          }}
        >
          <Grid container spacing={3}>
            {/* Plan Name */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Plan Name"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                error={!!errors.planName}
                helperText={errors.planName}
                onKeyDown={allowOnlyAlphabets}
                onPaste={handlePasteOnlyAlpha}
                sx={modernFieldStyle}
              />
            </Grid>

            {/* Asset Allocation Section */}
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                gutterBottom
                sx={{ color: "var(--text-color)" }}
              >
                Asset Allocation
              </Typography>
              {modalMode !== "edit" && (
                <OutlinedButton
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: "8px", fontWeight: 600 }}
                  startIcon={<AddIcon />}
                  onClick={addAsset}
                >
                  Add Asset
                </OutlinedButton>
              )}

              {/* asset rows */}
              <Stack spacing={1.5}>
                {assets.map((asset, i) => (
                  <Box
                    key={asset.index}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    {/* Select Asset */}
                    <FormControl
                      sx={{
                        flex: isMobile ? "unset" : 2,
                        width: isMobile ? "100%" : "auto",
                        ...modernFieldStyle,
                      }}
                    >
                      <InputLabel>Select Asset</InputLabel>
                      <Select
                        value={asset.currencyId}
                        label="Select Asset"
                        disabled={modalMode === "edit"}
                        onChange={(e) => {
                          const newAssets = [...assets];
                          newAssets[i].currencyId = e.target.value;
                          setAssets(newAssets);
                        }}
                        error={!!errors[`asset_${i}_currencyId`]}
                      >
                        <MenuItem value="">Select</MenuItem>
                        {portfolioAssetsForCounter.map(
                          (a) =>
                            !assets.some(
                              (item, index) =>
                                item.currencyId === a.currencyId &&
                                index !== i
                            ) && (
                              <MenuItem
                                key={a.currencyId}
                                value={a.currencyId}
                              >
                                {a.currencyCode}
                              </MenuItem>
                            )
                        )}
                      </Select>
                    </FormControl>

                    {/* Allocation % */}
                    <TextField
                      type="number"
                      label="Allocation (%)"
                      value={asset.assetAllocation}
                      disabled={modalMode === "edit"}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const newAssets = [...assets];
                        newAssets[i].assetAllocation = value;
                        setAssets(newAssets);

                        const newErrors = { ...errors };
                        if (value < 10) {
                          newErrors[`asset_${i}_allocation`] =
                            "Minimum allocation is 10%";
                        } else {
                          delete newErrors[`asset_${i}_allocation`];
                        }
                        setErrors(newErrors);
                      }}
                      error={!!errors[`asset_${i}_allocation`]}
                      helperText={errors[`asset_${i}_allocation`]}
                      onKeyDown={allowNumber}
                      sx={{
                        flex: isMobile ? "unset" : 2,
                        width: isMobile ? "100%" : "auto",
                        ...modernFieldStyle,
                      }}
                    />

                    {/* Delete */}
                    <Box
                      sx={{
                        flex: isMobile ? "unset" : 1,
                        display: "flex",
                        alignItems: "center",
                        pt: isMobile ? 0 : 0.5,
                      }}
                    >
                      {modalMode !== "edit" && asset.index !== 0 && (
                        <IconButton
                          onClick={() => removeAsset(asset.index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  mt: 1.25,
                  display: "block",
                  color: totalAllocation === 100 ? btncolor : "error.main",
                  fontWeight: 600,
                }}
              >
                {errors.totalAllocation ||
                  `The sum of allocation is ${totalAllocation}%${
                    totalAllocation === 100 ? "" : ", must add up to 100%."
                  }`}
              </Typography>
            </Grid>

            {/* Recurring Amount & Asset */}
            <Grid size={6}>
              <TextField
                fullWidth
                label="Recurring Amount"
                value={recurringAmount}
                onChange={(e) => setRecurringAmount(e.target.value)}
                error={!!errors.recurringAmount}
                helperText={errors.recurringAmount}
                onKeyDown={allowNumber}
                sx={modernFieldStyle}
              />
              {!(
                Number(recurringAmount) < Number(spotWalletBalance) ||
                Number(recurringAmount) < Number(earnWalletBalance)
              ) &&
                recurringAmount && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block", fontWeight: 500 }}
                  >
                    You do not have sufficient balance!
                  </Typography>
                )}
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth sx={modernFieldStyle}>
                <InputLabel>Recurring Asset</InputLabel>
                <Select
                  value={recurringAssetId}
                  label="Recurring Asset"
                  onChange={(e) => {
                    setRecurringAssetId(e.target.value);
                    getWalletBalance(e.target.value);
                  }}
                  error={!!errors.recurringAssetId}
                >
                  <MenuItem value="">Select</MenuItem>
                  {portfolioAssetsForBase.map((a) => (
                    <MenuItem key={a.currencyId} value={a.currencyId}>
                      {a.currencyCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Wallet Selection */}
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                gutterBottom
                sx={{ color: "var(--text-color)" }}
              >
                Subscription amount will be deducted from the selected wallet
              </Typography>
              <TableContainer
                component={Paper}
                sx={{
                  ...modernTableContainer,
                  borderRadius: "10px",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {["Wallet", "Balance", "Selection"].map((h) => (
                        <TableCell key={h} sx={modernTableHeadCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={modernTableBodyRow}>
                      <TableCell sx={modernTableBodyCell}>Spot Wallet</TableCell>
                      <TableCell sx={modernTableBodyCell}>
                        {spotWalletBalance}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...modernTableBodyCell,
                          color: "var(--text-color)",
                          opacity: 0.6,
                          fontStyle: "italic",
                        }}
                      >
                        Default
                      </TableCell>
                    </TableRow>
                    <TableRow sx={modernTableBodyRow}>
                      <TableCell sx={modernTableBodyCell}>Earn Wallet</TableCell>
                      <TableCell sx={modernTableBodyCell}>
                        {earnWalletBalance}
                      </TableCell>
                      <TableCell sx={modernTableBodyCell}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedWallet === "earn"}
                              onChange={() =>
                                setSelectedWallet(
                                  selectedWallet === "earn" ? "spot" : "earn"
                                )
                              }
                              sx={{ color: btncolor }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ color: "var(--text-color)" }}
                            >
                              Use
                            </Typography>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* One-Time Toggle */}
            <Grid size={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "var(--body-color)",
                  borderRadius: "10px",
                  border: "1px solid rgba(125,125,125,0.15)",
                  px: 2,
                  py: 1.25,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: "var(--text-color)" }}
                >
                  Recurring Period Selection
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--text-color)", opacity: 0.75 }}
                  >
                    One-time
                  </Typography>
                  <Switch
                    checked={isOneTime}
                    onChange={() => setIsOneTime(!isOneTime)}
                    color="primary"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Frequency Selection */}
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                gutterBottom
                sx={{ color: "var(--text-color)" }}
              >
                Select Frequency
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[...recurringPeriodsOther, ...recurringPeriodsHours].map(
                  (period) => (
                    <Button
                      key={period.id}
                      variant={
                        selectedRecurringPeriodId === period.id
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => setSelectedRecurringPeriodId(period.id)}
                      sx={{
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        bgcolor:
                          selectedRecurringPeriodId === period.id
                            ? btncolor
                            : "transparent",
                        borderColor: btncolor,
                        color:
                          selectedRecurringPeriodId === period.id
                            ? "#fff"
                            : "var(--text-color)",
                        "&:hover": {
                          bgcolor:
                            selectedRecurringPeriodId === period.id
                              ? btncolor
                              : alpha(btncolor || "#1976d2", 0.1),
                        },
                      }}
                    >
                      {period.name}
                    </Button>
                  )
                )}
              </Box>
            </Grid>

            {/* Weekly Day Selector */}
            {(selectedPeriodName.includes("Weekly") ||
              selectedPeriodName.includes("Bi") ||
              isWeekly) && (
              <Grid size={12}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  gutterBottom
                  sx={{ color: "var(--text-color)" }}
                >
                  Repeat On
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {daysOfWeeks.map((day) => (
                    <Button
                      key={day}
                      variant={
                        daysSelected.includes(day) ? "contained" : "outlined"
                      }
                      onClick={() => toggleDay(day)}
                      sx={{
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        minWidth: 52,
                        bgcolor: daysSelected.includes(day)
                          ? btncolor
                          : "transparent",
                        borderColor: btncolor,
                        color: daysSelected.includes(day)
                          ? "#fff"
                          : "var(--text-color)",
                        "&:hover": {
                          bgcolor: daysSelected.includes(day)
                            ? btncolor
                            : alpha(btncolor || "#1976d2", 0.1),
                        },
                      }}
                    >
                      {day}
                    </Button>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Monthly — Day of Month */}
            {isMonthly && (
              <Grid size={4}>
                <TextField
                  label="Day of Month (1-31)"
                  type="number"
                  value={dateOfMonth}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.length > 2) return;
                    if (val.startsWith("0") && val.length > 1) {
                      val = val.replace(/^0+/, "");
                    }
                    if (val === "" || (Number(val) >= 1 && Number(val) <= 31)) {
                      setDateOfMonth(val);
                    }
                  }}
                  inputProps={{ maxLength: 2 }}
                  error={!!errors.dateOfMonth}
                  helperText={errors.dateOfMonth}
                  sx={modernFieldStyle}
                />
              </Grid>
            )}

            {/* Time Selection */}
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "var(--text-color)", mb: 1.5 }}
              >
                Time to Invest (Local Time)
              </Typography>
              <Grid container spacing={1} alignItems="center">
                <Grid size={{ xs: 5, md: 2 }}>
                  <TextField
                    type="number"
                    label="HH"
                    value={localTimeHour}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val === "") {
                        setLocalTimeHour("");
                        return;
                      }
                      if (val.length > 2) return;
                      const num = Number(val);
                      if (num >= 0 && num <= 23) {
                        setLocalTimeHour(val);
                      }
                    }}
                    inputProps={{ min: 0, max: 24, maxLength: 2 }}
                    error={!!errors.localTimeHour}
                    helperText={errors.localTimeHour}
                    sx={modernFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2, md: 2 }} textAlign="center">
                  <Typography
                    sx={{ color: "var(--text-color)", fontWeight: 700 }}
                  >
                    :
                  </Typography>
                </Grid>
                <Grid size={{ xs: 5, md: 2 }}>
                  <TextField
                    type="number"
                    label="MM"
                    value={localTimeMin}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val === "") {
                        setLocalTimeMin("");
                        return;
                      }
                      if (val.length > 2) return;
                      const num = Number(val);
                      if (num >= 0 && num <= 59) {
                        setLocalTimeMin(val);
                      }
                    }}
                    inputProps={{ min: 0, max: 59, maxLength: 2 }}
                    error={!!errors.localTimeMin}
                    helperText={errors.localTimeMin}
                    sx={modernFieldStyle}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* First Cycle Info */}
            <Grid size={12}>
              <Box
                sx={{
                  backgroundColor: alpha(btncolor || "#1976d2", 0.08),
                  border: `1px solid ${alpha(btncolor || "#1976d2", 0.2)}`,
                  borderRadius: "8px",
                  px: 2,
                  py: 1.25,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "var(--text-color)", opacity: 0.8 }}
                >
                  {firstCycleMessage}
                </Typography>
              </Box>
            </Grid>

            {/* Terms */}
            <Grid size={12}>
              <FormControl error={!!errors.agreeTerms}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      sx={{ color: btncolor }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: "var(--text-color)" }}
                    >
                      I have read and agree to&nbsp;
                      <Link to={termsLink} target="_blank">
                        {brokerBasicInfo.exchange} Invest Agreement
                      </Link>
                    </Typography>
                  }
                />
                {errors.agreeTerms && (
                  <FormHelperText>{errors.agreeTerms}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "var(--popupbody-color)",
            borderTop: "1px solid rgba(125,125,125,0.15)",
          }}
        >
          <OutlinedButton
            variant="outlined"
            onClick={handleClosePortFolioCreateModal}
            sx={{ borderRadius: "10px", fontWeight: 600, px: 3 }}
          >
            Cancel
          </OutlinedButton>
          <ContainedButton
            variant="contained"
            onClick={handleCreatePortfolio}
            disabled={!isFormValid()}
            sx={{ borderRadius: "10px", fontWeight: 600, px: 3 }}
          >
            Invest Now
          </ContainedButton>
        </DialogActions>
      </Dialog>

      {/* ── Success Dialog ── */}
      <Dialog
        open={successModal}
        onClose={() => setSuccessModal(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...dialogTitleStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
            }}
          >
            <Box sx={{ width: 32 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "var(--text-color)" }}
            >
              Success
            </Typography>
            <IconButton
              onClick={() => setSuccessModal(false)}
              size="small"
              sx={{ color: "var(--text-color)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: "var(--popupbody-color)",
            px: 3,
            pt: 3,
          }}
        >
          {/* Success icon & message */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: alpha("#4caf50", 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <CheckIcon sx={{ fontSize: 36, color: "#4caf50" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontStyle: "italic",
                color: "var(--text-color)",
                opacity: 0.85,
                textAlign: "center",
              }}
            >
              Investment Portfolio created successfully!
            </Typography>
          </Box>

          {/* Details */}
          <Box
            sx={{
              backgroundColor: "var(--body-color)",
              borderRadius: "10px",
              border: "1px solid rgba(125,125,125,0.15)",
              p: 2,
            }}
          >
            {[
              {
                label: "Create Date",
                value: new Date().toString(),
              },
              {
                label: "Subscription Amount",
                value: `${createPortfolioPayload.investmentAmount} ${
                  getCurrencyNameById(createPortfolioPayload.invCurrencyId)
                    .currencyCode || ""
                }`,
              },
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.75,
                  borderBottom: "1px solid rgba(125,125,125,0.1)",
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "var(--text-color)", opacity: 0.65 }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "var(--text-color)", fontWeight: 600 }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}

            {/* Allocation rows */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "var(--text-color)", opacity: 0.65 }}
              >
                Allocation
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {createPortfolioPayload.investmentAssets?.map((l, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ color: "var(--text-color)", fontWeight: 600 }}
                  >
                    {getCurrencyNameById(l.currencyId).currencyCode}{" "}
                    {l.assetAllocation}%
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 1.5,
            py: 2,
            backgroundColor: "var(--popupbody-color)",
            borderTop: "1px solid rgba(125,125,125,0.15)",
          }}
        >
          <ContainedButton
            variant="contained"
            onClick={() => setSuccessModal(false)}
            sx={{ borderRadius: "10px", fontWeight: 600 }}
          >
            Back to portfolio invest
          </ContainedButton>
          <ContainedButton
            variant="contained"
            href="/portfolio-wallet"
            onClick={() => setSuccessModal(false)}
            sx={{ borderRadius: "10px", fontWeight: 600 }}
          >
            View Wallet
          </ContainedButton>
        </DialogActions>
      </Dialog>

      {/* ── View Allocation Dialog ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { ...dialogPaper, minWidth: { xs: "90%", sm: 540 } } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...dialogTitleStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
            }}
          >
            <Box sx={{ width: 32 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                color: "var(--text-color)",
              }}
            >
              Portfolio Allocation
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "var(--text-color)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "var(--popupbody-color)",
          }}
        >
          {/* Pie chart */}
          <Box sx={{ height: 360, mb: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={portfolioPlanDetails}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={70}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(1)}%`
                  }
                >
                  {portfolioPlanDetails.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{ borderRadius: 6 }}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Allocation breakdown table */}
          <TableContainer
            component={Paper}
            sx={{ ...modernTableContainer, mb: 3 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={modernTableHeadRow}>
                  <TableCell sx={modernTableHeadCell}>Asset</TableCell>
                  <TableCell sx={modernTableHeadCell}>Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {portfolioPlanDetails.map((plan) => (
                  <TableRow key={plan.name} sx={modernTableBodyRow}>
                    <TableCell sx={modernTableBodyCell}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <img
                          src={`${ASSETICONBASEURL}${plan.name}.png`}
                          alt={plan.name}
                          width={24}
                          height={24}
                          style={{ borderRadius: "50%" }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "var(--text-color)" }}
                        >
                          {plan.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: btncolor || "var(--text-color)" }}
                      >
                        {plan.value}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            mt={1}
            display="flex"
            justifyContent="center"
            gap={1.5}
          >
            <OutlinedButton
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: "10px", fontWeight: 600, px: 4 }}
            >
              Cancel
            </OutlinedButton>
            <ContainedButton
              variant="contained"
              onClick={() => openPortfolioEditModal(selectedPlan?.id)}
              sx={{ borderRadius: "10px", fontWeight: 600, px: 4 }}
            >
              Copy
            </ContainedButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PortfolioInvestPage;
